import { Navigation } from "@/components/Navigation"
import { Footer } from "@/components/Footer"
import { Heart, Users, HandHeart, Globe, Scale, ClipboardCheck, Mail } from "lucide-react"
import { SEO } from "@/components/SEO"

const HumanRightsStatement = () => {
    return (
        <div className="min-h-screen bg-white font-sans text-slate-900">
            <SEO
                title="Human Rights Statement | RSA Tech Group"
                description="RSA Tech Group's commitment to upholding human rights across our operations, workforce, and supply chain."
            />
            <Navigation />

            {/* Hero Section */}
            <div className="relative pt-48 pb-20 overflow-hidden bg-gradient-to-b from-[#0F172A] to-[#09090b]">
                <div className="absolute inset-0 pointer-events-none">
                    <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-blue-900/10 rounded-full blur-[120px] opacity-40 mix-blend-screen" />
                    <div className="absolute bottom-0 left-[-100px] w-[500px] h-[500px] bg-indigo-900/10 rounded-full blur-[100px] opacity-30" />
                </div>
                <div className="section-container relative z-10 text-center max-w-4xl mx-auto">
                    <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight text-white">
                        Human Rights Statement
                    </h1>
                    <p className="text-lg md:text-xl text-gray-300 leading-relaxed max-w-2xl mx-auto">
                        Our commitment to respecting and promoting human rights in every aspect of our business operations.
                    </p>
                    <div className="mt-8 text-sm text-gray-400 font-medium tracking-wide uppercase">
                        Last Updated: January 2026
                    </div>
                </div>
            </div>

            {/* Main Content */}
            <section className="py-24 bg-slate-50 relative overflow-hidden">
                <div className="absolute inset-0 bg-[url('/digital-transform-bg-rectangles.png')] bg-cover bg-center opacity-[0.03]" />

                <div className="section-container relative z-10 max-w-4xl mx-auto">
                    <div className="bg-white/80 backdrop-blur-md border border-slate-200 rounded-2xl p-8 md:p-12 shadow-sm">

                        {/* 1. Our Commitment */}
                        <div className="mb-12">
                            <div className="flex items-center gap-3 mb-6">
                                <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center text-blue-600">
                                    <Heart className="w-5 h-5" />
                                </div>
                                <h2 className="text-2xl font-bold text-slate-900">1. Our Commitment</h2>
                            </div>
                            <div className="space-y-4 text-slate-600 leading-relaxed pl-3 md:pl-14">
                                <p>
                                    RSA Tech Group is committed to respecting and promoting human rights as a fundamental part of our corporate values. We believe that technology companies have a unique responsibility to ensure their innovations and operations contribute positively to society.
                                </p>
                                <p>
                                    Our approach is guided by the United Nations Guiding Principles on Business and Human Rights, the Universal Declaration of Human Rights, and the International Labour Organization's core conventions. We strive to identify, prevent, and mitigate adverse human rights impacts connected to our business activities.
                                </p>
                            </div>
                        </div>

                        {/* 2. Core Principles */}
                        <div className="mb-12">
                            <div className="flex items-center gap-3 mb-6">
                                <div className="w-10 h-10 rounded-lg bg-indigo-50 flex items-center justify-center text-indigo-600">
                                    <Scale className="w-5 h-5" />
                                </div>
                                <h2 className="text-2xl font-bold text-slate-900">2. Core Principles</h2>
                            </div>
                            <div className="space-y-4 text-slate-600 leading-relaxed pl-3 md:pl-14">
                                <p>Our human rights commitment is built on the following core principles:</p>
                                <ul className="list-disc pl-5 space-y-2">
                                    <li><strong>Dignity & Respect:</strong> Every individual is treated with dignity and respect, regardless of race, gender, religion, national origin, age, disability, sexual orientation, or any other protected characteristic.</li>
                                    <li><strong>Non-Discrimination:</strong> We prohibit discrimination in all forms across our operations, hiring, and business relationships.</li>
                                    <li><strong>Freedom from Forced Labor:</strong> We do not tolerate forced labor, child labor, or human trafficking in any part of our business or supply chain.</li>
                                    <li><strong>Freedom of Association:</strong> We respect employees' rights to freedom of association and collective bargaining in accordance with applicable laws.</li>
                                    <li><strong>Privacy & Data Rights:</strong> We recognize the right to privacy as a fundamental human right and design our products and services accordingly.</li>
                                </ul>
                            </div>
                        </div>

                        {/* 3. Labor Practices */}
                        <div className="mb-12">
                            <div className="flex items-center gap-3 mb-6">
                                <div className="w-10 h-10 rounded-lg bg-emerald-50 flex items-center justify-center text-emerald-600">
                                    <Users className="w-5 h-5" />
                                </div>
                                <h2 className="text-2xl font-bold text-slate-900">3. Labor Practices</h2>
                            </div>
                            <div className="space-y-4 text-slate-600 leading-relaxed pl-3 md:pl-14">
                                <p>We are committed to fair and ethical labor practices across our organization:</p>
                                <ul className="list-disc pl-5 space-y-2">
                                    <li><strong>Fair Compensation:</strong> We provide competitive wages and benefits that meet or exceed applicable legal requirements and industry standards.</li>
                                    <li><strong>Safe Working Conditions:</strong> We maintain safe, healthy, and productive working environments for all employees, whether in-office or remote.</li>
                                    <li><strong>Work-Life Balance:</strong> We promote reasonable working hours and respect employees' rights to rest and leisure.</li>
                                    <li><strong>Professional Development:</strong> We invest in employee growth through training, mentorship, and career advancement opportunities.</li>
                                    <li><strong>Anti-Harassment:</strong> We maintain a zero-tolerance policy for harassment, bullying, or retaliation of any kind.</li>
                                </ul>
                            </div>
                        </div>

                        {/* 4. Diversity & Inclusion */}
                        <div className="mb-12">
                            <div className="flex items-center gap-3 mb-6">
                                <div className="w-10 h-10 rounded-lg bg-purple-50 flex items-center justify-center text-purple-600">
                                    <HandHeart className="w-5 h-5" />
                                </div>
                                <h2 className="text-2xl font-bold text-slate-900">4. Diversity & Inclusion</h2>
                            </div>
                            <div className="space-y-4 text-slate-600 leading-relaxed pl-3 md:pl-14">
                                <p>
                                    RSA Tech Group believes that diverse teams build better products and create stronger companies. We are committed to building a workforce and culture that reflects the diversity of the communities we serve.
                                </p>
                                <ul className="list-disc pl-5 space-y-2">
                                    <li>We actively recruit from diverse talent pools and implement equitable hiring practices.</li>
                                    <li>We foster an inclusive workplace culture where every employee feels valued, heard, and empowered to contribute.</li>
                                    <li>We ensure our AI products and services are developed with fairness and bias-mitigation principles to prevent discriminatory outcomes.</li>
                                    <li>We regularly review our diversity metrics and publish progress toward our inclusion goals.</li>
                                </ul>
                            </div>
                        </div>

                        {/* 5. Supply Chain Responsibility */}
                        <div className="mb-12">
                            <div className="flex items-center gap-3 mb-6">
                                <div className="w-10 h-10 rounded-lg bg-orange-50 flex items-center justify-center text-orange-600">
                                    <Globe className="w-5 h-5" />
                                </div>
                                <h2 className="text-2xl font-bold text-slate-900">5. Supply Chain Responsibility</h2>
                            </div>
                            <div className="space-y-4 text-slate-600 leading-relaxed pl-3 md:pl-14">
                                <p>
                                    We extend our human rights expectations to our suppliers, partners, and vendors. We expect all business partners to:
                                </p>
                                <ul className="list-disc pl-5 space-y-2">
                                    <li>Comply with all applicable labor laws and regulations.</li>
                                    <li>Prohibit forced labor, child labor, and human trafficking in their operations.</li>
                                    <li>Provide safe and healthy working conditions for their employees.</li>
                                    <li>Respect workers' rights to fair wages, reasonable working hours, and freedom of association.</li>
                                </ul>
                                <p>
                                    We conduct due diligence on our supply chain partners and may terminate relationships with those who fail to meet our human rights standards.
                                </p>
                            </div>
                        </div>

                        {/* 6. Reporting & Accountability */}
                        <div className="mb-12">
                            <div className="flex items-center gap-3 mb-6">
                                <div className="w-10 h-10 rounded-lg bg-teal-50 flex items-center justify-center text-teal-600">
                                    <ClipboardCheck className="w-5 h-5" />
                                </div>
                                <h2 className="text-2xl font-bold text-slate-900">6. Reporting & Accountability</h2>
                            </div>
                            <div className="space-y-4 text-slate-600 leading-relaxed pl-3 md:pl-14">
                                <p>
                                    We encourage employees, contractors, and external stakeholders to report any concerns about potential human rights violations without fear of retaliation.
                                </p>
                                <ul className="list-disc pl-5 space-y-2">
                                    <li>Reports can be made directly to our leadership team or through our designated ethics channel.</li>
                                    <li>All reports are investigated promptly, confidentially, and thoroughly.</li>
                                    <li>We maintain a strict non-retaliation policy for anyone who raises a concern in good faith.</li>
                                    <li>Our leadership team reviews human rights performance annually and reports findings to the Board.</li>
                                </ul>
                            </div>
                        </div>

                        {/* 7. Contact Us */}
                        <div className="mt-16 pt-12 border-t border-slate-100">
                            <h2 className="text-2xl font-bold text-slate-900 mb-6">7. Contact Us</h2>
                            <p className="text-slate-600 mb-6">
                                If you have questions about our human rights practices or wish to report a concern, please contact us:
                            </p>
                            <div className="flex flex-col gap-2 pl-3 md:pl-0">
                                <div className="font-bold text-slate-900">RSA Tech Group</div>
                                <div className="flex items-center gap-3 text-slate-800 font-medium">
                                    <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center">
                                        <Mail className="w-4 h-4" />
                                    </div>
                                    <a href="mailto:info@rsatechgroup.com" className="hover:text-blue-600 transition-colors">info@rsatechgroup.com</a>
                                </div>
                            </div>
                        </div>

                    </div>
                </div>
            </section>

            <Footer />
        </div>
    )
}

export default HumanRightsStatement
